import React, { useCallback, useEffect, useRef, useState } from 'react';
import { NavLink } from 'react-router-dom';
import { useClickAway, useWindowSize } from 'react-use';
import cn from 'classnames';

import isOkRange from 'bl/kpi/isOkRange.bl';
import Permitted from 'components/core/Permitted.component';
import Button from 'components/form/Button.component';
import ToggleSwitch from 'components/form/ToggleSwitch.component';
import CalendarRangedInput from 'components/generic/CalendarRangedInput.component';
import DropdownFoldout from 'components/generic/DropdownFoldout.component';
import Icon from 'components/generic/Icon.component';
import CN_KSF from 'components/kpi/KpiSpecificPickers.module.scss';
import MonthYearPicker from 'components/kpi/MonthYearPicker.component';
import ToggleVat from 'components/kpi/ToggleVat.component';
import PERM from 'constants/core/perm.const';
import LINK from 'constants/link.const';
import MS from 'constants/time/ms.const';
import TIK from 'constants/time/timeIntervalKind.const';
import TIL from 'constants/time/timeIntervalLabel.const';
import MOBILE_VIEWPORT from 'constants/ui/mobileViewport.const';
import { useCheckmap } from 'hooks/useCheckmap.hook';
import useTip from 'hooks/useTip.hook';
import { useGilt, useTranslation } from 'providers/GILT.provider';
import { useCurrentSalonId } from 'providers/SalonBasicInfo.provider';
import { useTheme } from 'providers/Theme.provider';
import alen from 'utils/arrays/alen.util';
import fromAnyToTrimmed from 'utils/converters/any/fromAnyToTrimmed.util';
import fromAnyToBool from 'utils/converters/fromAnyToBool.util';
import fromEntriesToObject from 'utils/converters/object/fromEntriesToObject.util';
import fromObjectToValues from 'utils/converters/object/fromObjectToValues.util';
import { fromAnyDateToIso, fromAnyDateToIsoDayEnd } from 'utils/converters/time/from.util';
import { nowAsIso } from 'utils/date/now.util';
import call from 'utils/functions/call.util';
import alwaysNoop from 'utils/functions/nullary/alwaysNoop.util';
import linkTo from 'utils/links/linkTo.util';
import logger from 'utils/logger.util';
import isFullArray from 'utils/predicates/array/isFullArray.util';
import { isTimeEqualWithin } from 'utils/predicates/time/compare.util';
import createTimeInterval from 'utils/predicates/time/createTimeInterval.util';

import KpiHowToTooltip from './KpiHowToTooltip.component';

import CN from './RangeFilter.module.scss';

const L = logger('RangeFilter'); // eslint-disable-line no-unused-vars

const USE_SHORT_NAME = false;
const USE_APPLY_BUTTON = true;
const RENDER_COMPARE_PICKER = false;

const ORDERED_KINDS = Object.freeze([
  TIK.monthToDate,
  TIK.monthLast,
  TIK.monthThis,
  TIK.monthNext,
  TIK.weekToDate,
  TIK.weekLast,
  TIK.weekThis,
  TIK.weekNext,
  TIK.dayYester,
  TIK.dayToday,
  TIK.dayMorrow,
]);

const TIK_INIT = Object.freeze({
  ...fromEntriesToObject(fromObjectToValues(TIK).map((key) => [key, false])),
  [TIK.monthThis]: true,
  [TIK.monthToDate]: true,
});

const determineSelectedText = (options) => {
  const { array, t } = options;
  return array.length > 0 ? array.map((key) => t(TIL[key])).join(', ') : t('staffKpisReports.kpiReports.rangeFilter.selectPresetDate');
};

const QuickFilter = ({ kind: kindFromProps, onClick, selected }) => {
  const { t } = useTranslation();

  const kind = fromAnyToTrimmed(kindFromProps);
  const label = t(TIL[kind]);

  const handleClick = useCallback(() => void call('QuickFilter.onClick', onClick, { kind, label, selected: !selected }), [label, kind, onClick, selected]);

  return (
    <div data-bem="QuickFilter" data-kind={kind} className={CN.gap}>
      <ToggleSwitch tag="QuickFilter" selected={selected} onClick={handleClick} name={label} size="small" />
    </div>
  );
};

const isOkRanges = (ranges) => isFullArray(ranges) && ranges.every((range) => isOkRange(range));

const isEquivalent = (aRanges, bRanges) => {
  const len = alen(aRanges);

  if (len !== alen(bRanges)) return false;

  for (let i = 0; i < len; i += 1) {
    const a = aRanges[i];
    const b = bRanges[i];

    if (a?.kind !== b?.kind) return false;
    if (!isTimeEqualWithin(MS.second, a?.raw, b?.raw)) return false;
  }

  return true;
};

const RangeFilter = ({ onChange }) => {
  const { isDarkMode } = useTheme();
  const { t } = useTranslation();
  const { isSalonWithVAT } = useGilt();
  const salonId = useCurrentSalonId();
  const { width } = useWindowSize();

  const isMobileViewport = width <= MOBILE_VIEWPORT;

  const linkToEditKpi = linkTo({ pattern: LINK.hrEditKpi, params: { salonId }, fault: '#' });

  const [localCustom, setLocalCustom] = useState(null);
  const { val: isCustomRange, nay: selectQuickRange, yay: selectCustomRange } = useTip(false);
  const { val: isQuickDropdown, tip: toggleQuickDropdown, nay: closeQuickDropdown } = useTip(false);
  const [prevRanges, setPrevRanges] = useState(null);

  const { val: checkmap, tip: toggleQuick, tKeys: kinds, everyFalse: clearQuick } = useCheckmap(TIK_INIT);

  const kindsCount = alen(kinds);
  const hasQuickRangeKinds = fromAnyToBool(kindsCount);

  const handleQuickButton = useCallback(
    (ev) => {
      selectQuickRange();
      toggleQuick(ev?.kind);
    },
    [selectQuickRange, toggleQuick],
  );

  const handleCustomApply = useCallback(
    (ev) => {
      clearQuick();
      selectCustomRange();

      const parseFailTo = nowAsIso();

      // NOTE: to feed back to the child component, use its original local bgn/end, no changes here
      // it may even work without modifying the ev.bgn and ev.end instead of ev.localBgn and ev.localEnd
      const bgn = ev?.localBgn;
      const end = ev?.localEnd;

      setLocalCustom(createTimeInterval({ kind: TIK.custom, custom: { bgn, end }, parseFailTo }));

      // for the filtering use, the bgn/end need to be stretched to the midnights of the days in question
      const isoBgn = fromAnyDateToIso(ev?.bgn);
      const isoEnd = fromAnyDateToIsoDayEnd(ev?.end);

      const event = {
        intervals: [createTimeInterval({ kind: TIK.custom, custom: { bgn: isoBgn, end: isoEnd }, parseFailTo })],
        kinds: [TIK.custom],
      };
      call('RangeFilter.onChange', onChange, event);
    },
    [clearQuick, onChange, selectCustomRange],
  );

  const handleCompare = useCallback((ev) => void L.debug('handleCompare()', isCustomRange, ev), [isCustomRange]);

  const quickDropdownRef = useRef(null);
  useClickAway(quickDropdownRef, closeQuickDropdown);

  useEffect(() => {
    if (isCustomRange) return;
    if (!kindsCount) return;

    const now = nowAsIso();
    const nextRanges = kinds.map((kind) => createTimeInterval({ kind, now, parseFailTo: nowAsIso() }));

    const isTooEarly = isOkRanges(prevRanges) && isOkRanges(nextRanges) && isEquivalent(prevRanges, nextRanges);
    L.debug('useEffect()', isTooEarly, prevRanges, nextRanges);
    if (isTooEarly) return;

    setPrevRanges(nextRanges);
    call('RangeFilter.onChange', onChange, { intervals: nextRanges, kinds, now });
  }, [isCustomRange, onChange, kinds, kindsCount, prevRanges]);

  L.debug('()', { isQuickDropdown, isCustomRange }, kinds, localCustom);

  return (
    <div
      data-bem="RangeFilter"
      className={cn({
        [CN.component]: true,
        [CN.dark]: isDarkMode,
      })}
    >
      <div className={CN.flexContainer}>
        <div className={CN.container}>
          <div className={CN.desktopElements}>
            <div className={CN.title} data-bem="RangeFilter__title-desktop">
              <div className={CN.iconContainer}>
                <Icon variant="calendarKpi" width="xl" />
              </div>
            </div>
            {ORDERED_KINDS.map((kind) => (
              <QuickFilter key={kind} kind={kind} selected={checkmap?.[kind]} onClick={handleQuickButton} />
            ))}
          </div>
          <div className={CN.tabletDropdown}>
            <div className={CN.title} data-bem="RangeFilter__title-tablet">
              <div className={CN.iconContainer}>
                <Icon variant="calendarKpi" width="xl" />
              </div>
              {`${t('staffKpisReports.kpiReports.rangeFilter.dateRange')}:`}
            </div>
            <div
              ref={quickDropdownRef}
              className={cn({
                [CN.quickButtonContainer]: true,
                [CN.higherZIndex]: isQuickDropdown,
              })}
              data-bem="RangeFilter__quick-button-container"
            >
              <Button tag="RangeFilter__quick-button" variant="quinary" active={isQuickDropdown} selected={!isQuickDropdown && hasQuickRangeKinds} height="medium" onClick={toggleQuickDropdown}>
                <div className={CN.buttonInner}>
                  <div className={CN.buttonInnerText}>
                    <Icon variant="clockCircleEmpty" width="sm" />
                    <div
                      className={cn({
                        [CN.text]: true,
                        [CN.bold]: hasQuickRangeKinds,
                      })}
                    >
                      {determineSelectedText({ array: kinds, t })}
                    </div>
                  </div>
                  <Icon variant="arrowTriangle" width="xs" />
                </div>
              </Button>

              {isQuickDropdown && (
                <DropdownFoldout width="maxInTabletP" variant="withFooter">
                  <DropdownFoldout.Body>
                    <div data-bem="RangeFilter__quick-dropdown-list" className={CN.scrollContainer}>
                      {ORDERED_KINDS.map((kind) => (
                        <div data-bem="RangeFilter__quick-dropdown-item" data-kind={String(kind)} className={CN.gap} key={kind}>
                          <ToggleSwitch tag="RangeFilter__toggle-switch" selected={checkmap?.[kind]} onClick={() => handleQuickButton({ kind })} name={t(TIL[kind])} size="small" variant="mobile" />
                        </div>
                      ))}
                    </div>
                  </DropdownFoldout.Body>
                  <DropdownFoldout.Footer>
                    <div className={CN.dropdownFooterContainer}>
                      {USE_APPLY_BUTTON && (
                        <Button variant="primaryForce" onClick={closeQuickDropdown}>
                          <span className={CN.buttonGap}>{t('common.buttons.doneUpp')}</span>
                        </Button>
                      )}
                    </div>
                  </DropdownFoldout.Footer>
                </DropdownFoldout>
              )}
            </div>
          </div>
        </div>

        <div
          data-bem="RangeFilter__kpi-specific-pickers"
          className={cn({
            [CN_KSF.component]: true,
            [CN_KSF.dark]: isDarkMode,
          })}
        >
          <div className={CN_KSF.customRangeContainer}>
            <div className={CN_KSF.buttonInner}>
              <CalendarRangedInput
                tag="KpiSpecificPickers__CustomRange"
                onApply={handleCustomApply}
                selected={isCustomRange}
                label={isMobileViewport && USE_SHORT_NAME ? t('staffKpisReports.kpiReports.rangeFilter.dateRange') : t('staffKpisReports.kpiReports.rangeFilter.customDateRange')}
                isModalPopup
                bgn={isCustomRange ? localCustom?.bgnIso : void 1}
                end={isCustomRange ? localCustom?.endIso : void 1}
              />
            </div>
          </div>
          {RENDER_COMPARE_PICKER && (
            <div className={CN_KSF.compareRangeContainer}>
              <MonthYearPicker withOverlay autoclose onInit={selectQuickRange} onChange={handleCompare} unreleased="staffKpisReports.kpiReports.monthYearPicker.unreleased" />
            </div>
          )}
        </div>

        {isSalonWithVAT && <ToggleVat />}
        <div className={CN.displayKpiHowToTooltip} data-bem="RangeFilter__KpiHowToTooltip">
          <Permitted value={PERM.canEditKpisAllStaff}>
            <KpiHowToTooltip />
          </Permitted>
        </div>

        <div className={CN.navLinkContainerTablet}>
          <Permitted value={PERM.canEditKpisAllStaff}>
            <Button variant="primaryForce" onClick={alwaysNoop}>
              <NavLink to={linkToEditKpi}>
                <span className={CN.navLinkColor}>{t('staffKpisReports.kpiReports.customiseKpi')}</span>
              </NavLink>
            </Button>
          </Permitted>
        </div>
      </div>
    </div>
  );
};

export default RangeFilter;
